import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AuditIncidentListView from "src/components/pages/audit/auditIncidentListView/auditIncidentListView";
import AuditInputUpload from "src/components/pages/audit/auditInputUpload/auditInputUpload";
import AuditReviewView from "src/components/pages/audit/auditReview/auditReviewView";
import AuditDashboard from "src/components/pages/audit/auditDashboard/auditDashboard";
import AutomatedJobReview from "src/components/pages/automatedJobs/automatedJobReview";
import AutomatedJobs from "src/components/pages/automatedJobs/automatedJobs";
import AutomatedJobManagement from "src/components/pages/automatedJobs/automatedJobManagement";
import BatchProcess from "src/components/pages/batchProcess/batchProcess";
import DataExtraction from "src/components/pages/dataExtraction/dataExtraction";
import ModelManagement from "src/components/pages/modelManagement/modelManagement";
import ValidationWorkflow from "src/components/pages/validationWorkflow/groundTruthFileUploadView/validationWorkflow";
import ModelGroundTruthListView from "src/components/pages/validationWorkflow/groundTruthListView/modelGroundTruthListView";
import ModelGroundTruthReview from "src/components/pages/validationWorkflow/groundTruthReview/modelGroundTruthReview";
import {
  CPT_AUDITOR_LDAP_GROUP,
  CPT_AUDITOR_ADMIN_LDAP_GROUP,
  DUMMY_MODEL_ID,
  ADMIN_LDAP_GROUP,
  ENGINEER_LDAP_GROUP,
} from "src/constants";
import { getModelById, resetField } from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { canAccessVlmAudit } from "src/utils/permissionUtil";
import BatchRecordListView from "src/components/pages/batchProcess/batchReordListView/batchReordListView";
import BatchRecordReview from "src/components/pages/batchProcess/batchRecordReview/batchRecordReview";
import AuditQueue from "src/components/pages/auditModule/queue/AuditQueue";
import UseCaseList from "src/components/pages/auditModule/config/UseCaseList";
import UseCaseEditor from "src/components/pages/auditModule/config/UseCaseEditor";
import PermissionsPage from "src/components/pages/auditModule/config/PermissionsPage";
import ExportBuilder from "src/components/pages/auditModule/reports/ExportBuilder";
import {
  WorkspaceWithLayout,
  AuditPageLayout,
  ConfigRoute,
} from "src/components/pages/auditModule/shared/AuditWrappers";

const MainContent = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { selectedModelId } = useAppSelector((state) => state.modelSlice);

  const { ldap, alias, userLoading } = useAppSelector((state) => state.user);

  // Temporary beta-tester guard; replaced by the member-table check at launch.
  const canSeeVlmAudit = canAccessVlmAudit(ldap, alias);

  useEffect(() => {
    if (selectedModelId !== "" && selectedModelId !== DUMMY_MODEL_ID) {
      dispatch(getModelById(selectedModelId));
      dispatch(resetField());
    }
  }, [selectedModelId, location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<DataExtraction />} />
      <Route path="/dataExtraction" element={<DataExtraction />} />
      <Route path="/modelManagement" element={<ModelManagement />} />
      <Route path="/batchProcess" element={<BatchProcess />} />
      <Route path="/batchProcess/:batchJobId" element={<BatchRecordListView />} />
      <Route path="/batchProcess/:batchJobId/:sourceId" element={<BatchRecordReview />} />

      {/* Audit Workflow tab: original audit pages */}
      <>
        {ldap.includes(CPT_AUDITOR_LDAP_GROUP) && (
          <Route path="/audit" element={<AuditInputUpload />} />
        )}
        {ldap.includes(CPT_AUDITOR_LDAP_GROUP) && (
          <Route path="/audit/:auditWorkflowId" element={<AuditIncidentListView />} />
        )}
        {ldap.includes(CPT_AUDITOR_LDAP_GROUP) && (
          <Route path="/audit/:auditWorkflowId/:incidentId" element={<AuditReviewView />} />
        )}
      </>

      {/* VLM Audit Workflow tab: audit module (queue, workspace, configuration) */}
      {canSeeVlmAudit && (
        <>
          <Route path="/vlmAudit/workspace/:taskId" element={<WorkspaceWithLayout />} />
          {/* Configuration (Use Case Management + Permissions & Access): Admins and
            Program Owners only, resolved from the permissions registry. ConfigRoute
            waits for the role lookup and sends Auditors back to the queue. */}
          <Route
            path="/vlmAudit/config"
            element={
              <ConfigRoute>
                <UseCaseList />
              </ConfigRoute>
            }
          />
          <Route
            path="/vlmAudit/config/new"
            element={
              <ConfigRoute adminOnly>
                <UseCaseEditor />
              </ConfigRoute>
            }
          />
          <Route
            path="/vlmAudit/config/permissions"
            element={
              <ConfigRoute>
                <PermissionsPage />
              </ConfigRoute>
            }
          />
          <Route
            path="/vlmAudit/config/:useCaseId"
            element={
              <ConfigRoute>
                <UseCaseEditor />
              </ConfigRoute>
            }
          />
          {/* Export & Reports: Admins (all use cases) and Program Owners (their own);
            the backend scopes the data, ConfigRoute keeps Auditors out. */}
          <Route
            path="/vlmAudit/reports"
            element={<Navigate to="/vlmAudit/reports/export" replace />}
          />
          <Route
            path="/vlmAudit/reports/export"
            element={
              <ConfigRoute>
                <ExportBuilder />
              </ConfigRoute>
            }
          />
          <Route
            path="/vlmAudit/:tab?"
            element={
              <AuditPageLayout>
                <AuditQueue />
              </AuditPageLayout>
            }
          />
        </>
      )}
      {(ldap.includes(ADMIN_LDAP_GROUP) ||
        ldap.includes(ENGINEER_LDAP_GROUP) ||
        ldap.includes(CPT_AUDITOR_ADMIN_LDAP_GROUP)) && (
        <Route path="/auditAnalyzer" element={<AuditDashboard />} />
      )}

      <Route path="/validationWorkflow" element={<ValidationWorkflow />} />
      <Route path="/validationWorkflow/:modelId" element={<ModelGroundTruthListView />} />
      <Route path="/validationWorkflow/:modelId/:sourceId" element={<ModelGroundTruthReview />} />

      <Route path="/automatedJobs" element={<AutomatedJobs />} />
      {ldap.includes(ENGINEER_LDAP_GROUP) && (
        <Route path="/automatedJobs/manage" element={<AutomatedJobManagement />} />
      )}
      <Route path="/automatedJobs/:jobId" element={<AutomatedJobs />} />
      <Route path="/automatedJobs/:jobId/:sourceId" element={<AutomatedJobReview />} />

      {userLoading === "fulfilled" && <Route path="*" element={<Navigate to="/" replace />} />}
    </Routes>
  );
};

export default MainContent;
